import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePayments from '../hooks/usePayments';
import { useRole } from '../context/roleContext';
import Pagination from '../components/common/Pagination';
import PaymentsTable from '../components/payments/PaymentsTable';
import RecordPaymentModal from '../components/payments/RecordPaymentModal';
import EditDueDateModal from '../components/payments/EditDueDateModal';
import SendReminderModal from '../components/payments/SendReminderModal';
import PaymentHistoryModal from '../components/payments/PaymentHistoryModal';
import BulkActionModal from '../components/payments/BulkActionModal';

// Mirrors the backend whitelist in app/crud/payment.py — a value not in that
// list is rejected by the endpoint, so these must stay in step.
const SORTS = [
  { value: 'due_date_asc', label: 'Due date — earliest first' },
  { value: 'due_date_desc', label: 'Due date — latest first' },
  { value: 'challan_date_asc', label: 'Challan date — oldest first' },
  { value: 'challan_date_desc', label: 'Challan date — newest first' },
  { value: 'amount_desc', label: 'Challan amount — largest first' },
  { value: 'outstanding_desc', label: 'Outstanding — largest first' },
  { value: 'customer_asc', label: 'Customer — A to Z' },
];

const TABS = [
  { value: 'pending', label: 'Pending' },
  // Owed but not yet due — the complement of Pending among unpaid challans, so
  // the two tabs partition the receivables without overlapping.
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
];

export default function PaymentsTrackingPage() {
  const {
    challans, total, summary, loading, error,
    fetchChallans, fetchSummary,
    recordPayment, reversePayment, getHistory, updateDueDate, sendReminder,
    bulkMarkPaid, bulkUpdateDueDate, bulkSendReminders,
  } = usePayments();
  const { isSuperAdmin, isAdmin } = useRole();
  const canReverse = (isSuperAdmin && isSuperAdmin()) || (isAdmin && isAdmin());

  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [sort, setSort] = useState('due_date_asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [modal, setModal] = useState(null); // { type, row }
  // Selection is scoped to the rows currently on screen. Carrying it across pages
  // would let an operator settle challans they can no longer see, and "select all"
  // would quietly mean something different from what the header checkbox shows.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    const params = { status: statusFilter, sort, skip: (currentPage - 1) * pageSize, limit: pageSize };
    if (committedSearch) params.search = committedSearch;
    fetchChallans(params);
  }, [statusFilter, sort, currentPage, pageSize, committedSearch, fetchChallans]);

  useEffect(() => { load(); }, [load]);

  // The summary counts EVERY challan and ignores the tab, page and search, yet it
  // used to be refetched alongside the list — so paging or switching tabs re-ran a
  // full receivables aggregate that could not have changed. Fetch it on mount, and
  // again only after something actually moves money (see refreshAll).
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // After a payment, reversal or due-date change the cards ARE stale, so both.
  const refreshAll = useCallback(() => {
    load();
    fetchSummary();
  }, [load, fetchSummary]);

  // Any change to the underlying query replaces the rows, so a selection made
  // against the previous set is meaningless — and acting on it would be wrong.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, committedSearch, currentPage, pageSize, sort]);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((select) => {
    setSelectedIds(select ? new Set((challans || []).map((c) => c.sub_order_id)) : new Set());
  }, [challans]);

  const selectedRows = (challans || []).filter((c) => selectedIds.has(c.sub_order_id));

  const afterBulk = () => {
    setSelectedIds(new Set());
    refreshAll();
  };

  const onSearchChange = (v) => {
    setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setCommittedSearch(v); setCurrentPage(1); }, 400);
  };
  const changeTab = (v) => { setStatusFilter(v); setCurrentPage(1); };
  const closeModal = () => setModal(null);
  const afterAction = () => { closeModal(); refreshAll(); };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Counts only — the rupee outstanding sums were removed from the cards (they
  // summed GST-inclusive challan totals across every matching challan, producing
  // misleading crore-scale figures). Per-challan amounts remain in the table.
  const cards = summary ? [
    { label: 'Pending', value: summary.pending_count, cls: 'text-blue-700' },
    { label: 'Overdue', value: summary.overdue_count, cls: 'text-red-700' },
    { label: 'Due today+', value: summary.due_count, cls: 'text-amber-700' },
    // Matches the Upcoming tab. Counted apart from Pending on purpose: these are
    // owed but not yet due, so they are not being chased.
    { label: 'Upcoming', value: summary.upcoming_count ?? 0, cls: 'text-gray-600' },
    { label: 'Paid', value: summary.paid_count, cls: 'text-green-700' },
  ] : [];

  return (
    <div className="p-4 w-full">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">{c.label}</div>
            <div className={`text-xl font-bold ${c.cls}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.value} onClick={() => changeTab(t.value)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium ${statusFilter === t.value ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-500 whitespace-nowrap" htmlFor="payments-sort">
            Sort by
          </label>
          <select
            id="payments-sort"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
            className="px-3 py-1.5 rounded-lg border-2 border-gray-300 text-sm"
          >
            {SORTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input value={searchInput} onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search challan / order / customer"
            className="px-3 py-1.5 rounded-lg border-2 border-gray-300 text-sm w-72 max-w-full" />
        </div>
      </div>

      {/* A failed fetch used to be invisible: the table simply said "No challans
          found", which reads as an empty list rather than a broken request. */}
      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg border border-red-200 bg-red-50">
          <span className="text-sm text-red-800">{error}</span>
          <button
            type="button"
            onClick={refreshAll}
            className="px-3 py-1 rounded-lg bg-white border border-red-300 text-red-700 text-sm font-semibold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Bulk bar — only once something is selected, so the page is unchanged
          for the ordinary one-challan-at-a-time flow. */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50">
          <span className="text-sm font-semibold text-blue-900">
            {selectedIds.size} selected
          </span>
          <button onClick={() => setBulkAction('markPaid')}
            className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold">
            Mark paid
          </button>
          <button onClick={() => setBulkAction('dueDate')}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold">
            Change due date
          </button>
          <button onClick={() => setBulkAction('reminder')}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold">
            Send reminders
          </button>
          <button onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-600 text-sm">
            Clear
          </button>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <PaymentsTable
          rows={challans}
          loading={loading}
          failed={Boolean(error)}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          onRecord={(row) => setModal({ type: 'record', row })}
          onReminder={(row) => setModal({ type: 'reminder', row })}
          onEditDue={(row) => setModal({ type: 'due', row })}
          onHistory={(row) => setModal({ type: 'history', row })}
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={total}
          onPageChange={setCurrentPage}
          loading={loading}
          totalLabel="challans"
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
        />
      </div>

      {modal?.type === 'record' && (
        <RecordPaymentModal row={modal.row} onClose={closeModal}
          onSubmit={async (payload) => { const r = await recordPayment(modal.row.sub_order_id, payload); if (r.success) afterAction(); return r; }} />
      )}
      {modal?.type === 'due' && (
        <EditDueDateModal row={modal.row} onClose={closeModal}
          onSubmit={async (payload) => { const r = await updateDueDate(modal.row.sub_order_id, payload); if (r.success) afterAction(); return r; }} />
      )}
      {modal?.type === 'reminder' && (
        <SendReminderModal row={modal.row} onClose={closeModal}
          onSubmit={(channels) => sendReminder(modal.row.sub_order_id, channels)} />
      )}
      {bulkAction && (
        <BulkActionModal
          action={bulkAction}
          rows={selectedRows}
          onClose={(didApply) => { setBulkAction(null); if (didApply) afterBulk(); }}
          onSubmit={async (payload) => {
            if (bulkAction === 'markPaid') return bulkMarkPaid(payload);
            if (bulkAction === 'dueDate') return bulkUpdateDueDate(payload);
            return bulkSendReminders(payload);
          }}
        />
      )}
      {modal?.type === 'history' && (
        <PaymentHistoryModal row={modal.row} getHistory={getHistory} onClose={closeModal}
          canReverse={canReverse}
          onReverse={async (id) => { const r = await reversePayment(id); if (r.success) refreshAll(); return r; }} />
      )}
    </div>
  );
}
